/**
 * ⚖ Telling a DEAD SESSION apart from a runtime that is merely rotating.
 *
 * ## The problem this exists to solve
 *
 * WS close code **1008** (policy violation) is what the gateway sends for *both*
 * of these, and they need opposite handling:
 *
 * | situation | truth | correct UI |
 * |---|---|---|
 * | per-user runtime recreate — token rejected mid-rotation | TRANSIENT | keep reconnecting ("workspace updating") |
 * | the credential itself is expired/revoked | TERMINAL | stop, and say the session ended |
 *
 * Today both reconnect forever. That makes a permanently dead session render as
 * a transport hiccup and retry indefinitely against a guaranteed rejection —
 * the D-1 defect in `lexide-auth-failure-surfacing-miniplan.md`.
 *
 * ## ⚠ Why this is NOT "treat 1008 as unauthorized"
 *
 * That one-liner is the obvious fix and it is a REGRESSION: it would declare a
 * routine runtime roll a dead session and strand a lawyer who only had to wait
 * ten seconds. The miniplan's S4 is the acceptance bar precisely because the
 * dangerous failure here is the fix eating genuine reconnects — a wording change
 * that becomes an outage.
 *
 * ## The discriminator is EVIDENCE, not a guess
 *
 * A rotation resolves: the runtime returns and the next connect succeeds. A dead
 * credential does not, and `reconnectWithRefresh` re-fetches auth before every
 * attempt — so in Clerk mode a recoverable session self-heals within an attempt
 * or two, while a genuinely dead one keeps producing 1008 *after a fresh token
 * was fetched*. Repetition is therefore the observable difference, and waiting
 * for it costs a few seconds of the reconnect the user would have had anyway.
 *
 * **The burden of proof sits on declaring the session dead.** When in doubt this
 * returns `false` and the caller keeps today's behaviour.
 */

/**
 * Consecutive auth-class closes before we call the session dead.
 *
 * Three, deliberately: one is the ordinary rotation signal, two is a slow
 * rotation, and by the third we have re-fetched auth twice and still been
 * refused — which a rotation does not survive. Set too low, this eats real
 * reconnects (S4); too high, the lawyer waits longer on a session that is
 * already gone. The cost of being wrong is asymmetric, so it errs high.
 */
export const AUTH_CLOSE_TERMINAL_THRESHOLD = 3;

/** WS close code for policy/auth rejection — the only code this considers. */
export const WS_CLOSE_POLICY = 1008;

export interface AuthCloseInput {
  /** The close code just observed. */
  code: number;
  /** How many CONSECUTIVE auth-class closes have preceded this one. Reset to 0
   *  on any successful connect — that reset is what makes a rotation recover. */
  priorConsecutiveAuthCloses: number;
  threshold?: number;
}

/** Is this close in the auth class at all? Everything else is transport. */
export function isAuthClose(code: number): boolean {
  return code === WS_CLOSE_POLICY;
}

/**
 * Should the client declare the session TERMINALLY unauthorized and stop
 * retrying?
 *
 * ⚠ Returns `false` for every non-auth close code, no matter how many times it
 * repeats. A flapping network produces 1006 endlessly and must NEVER be
 * classified as an auth failure — that is S4, and misclassifying it would
 * strand a session that was going to recover on its own.
 */
export function isTerminalAuthFailure(input: AuthCloseInput): boolean {
  const { code, priorConsecutiveAuthCloses, threshold = AUTH_CLOSE_TERMINAL_THRESHOLD } = input;
  if (!isAuthClose(code)) return false;
  return priorConsecutiveAuthCloses + 1 >= threshold;
}
